import React, { useEffect, useState } from 'react';
import { Modal, Tabs, Table, Tag, Spin, Empty, Button, Popconfirm, message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { urunlerAPI } from '../services/api';
import { formatPara, formatSayi } from '../utils/format';
import dayjs from 'dayjs';

const hareketTipiRenk = {
  giris: 'green',
  alis_iptal: 'volcano',
  uretim_tuketim: 'blue',
  uretim_iptal: 'orange',
  uretim_cikti: 'geekblue',
  satis: 'red',
};

const hareketTipiEtiket = {
  giris: 'Giriş',
  alis_iptal: 'Alış İptal',
  uretim_tuketim: 'Üretim Tüketim',
  uretim_iptal: 'Üretim İptal',
  uretim_cikti: 'Üretim Çıktı',
  satis: 'Satış',
};

const stokColumns = [
  {
    title: 'Tarih',
    dataIndex: 'tarih',
    key: 'tarih',
    width: 160,
    render: (v) => v ? dayjs(v).format('DD.MM.YYYY HH:mm') : '-',
  },
  {
    title: 'Hareket Tipi',
    dataIndex: 'hareket_tipi',
    key: 'hareket_tipi',
    width: 150,
    render: (v) => <Tag color={hareketTipiRenk[v] || 'default'}>{hareketTipiEtiket[v] || v}</Tag>,
  },
  {
    title: 'Miktar',
    dataIndex: 'miktar',
    key: 'miktar',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatSayi(v, 2) : '-',
  },
  {
    title: 'Birim',
    dataIndex: 'birim',
    key: 'birim',
    width: 80,
  },
  {
    title: 'Üretim #',
    dataIndex: 'uretim_id',
    key: 'uretim_id',
    width: 90,
    render: (v) => v || '-',
  },
  {
    title: 'Satış #',
    dataIndex: 'satis_id',
    key: 'satis_id',
    width: 80,
    render: (v) => v || '-',
  },
  {
    title: 'Açıklama',
    dataIndex: 'aciklama',
    key: 'aciklama',
    ellipsis: true,
  },
];

const satisColumns = [
  {
    title: 'Tarih',
    dataIndex: 'tarih',
    key: 'tarih',
    width: 120,
    render: (v) => v ? dayjs(v).format('DD.MM.YYYY') : '-',
  },
  {
    title: 'Cari',
    dataIndex: 'cari_ad',
    key: 'cari_ad',
    width: 150,
    ellipsis: true,
    render: (v) => v || '-',
  },
  {
    title: 'Adet',
    dataIndex: 'adet',
    key: 'adet',
    width: 70,
    align: 'right',
  },
  {
    title: 'Birim Fiyat',
    dataIndex: 'birim_fiyat',
    key: 'birim_fiyat',
    width: 110,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'Maliyet',
    dataIndex: 'maliyet',
    key: 'maliyet',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'KDV Tutar',
    dataIndex: 'kdv_tutar',
    key: 'kdv_tutar',
    width: 100,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
  {
    title: 'KDV Dahil',
    dataIndex: 'kdv_dahil_tutar',
    key: 'kdv_dahil_tutar',
    width: 110,
    align: 'right',
    render: (v) => v != null ? formatPara(v) : '-',
  },
];

function UrunDetayModal({ open, urun, onClose, onDelete }) {
  const [loading, setLoading] = useState(false);
  const [stokData, setStokData] = useState([]);
  const [satisData, setSatisData] = useState([]);
  const [silLoading, setSilLoading] = useState(false);

  const handleSil = async () => {
    if (!urun?.id || !onDelete) return;
    setSilLoading(true);
    try {
      await onDelete(urun.id);
    } catch (err) {
      message.error('Ürün silinemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setSilLoading(false);
    }
  };

  useEffect(() => {
    if (open && urun?.id) {
      setLoading(true);
      urunlerAPI.getDetay(urun.id)
        .then((res) => {
          setStokData(res.data.stok_hareketleri || []);
          setSatisData(res.data.satis_kalemleri || []);
        })
        .catch(() => {
          setStokData([]);
          setSatisData([]);
        })
        .finally(() => setLoading(false));
    } else {
      setStokData([]);
      setSatisData([]);
    }
  }, [open, urun?.id]);

  const tabItems = [
    {
      key: 'stok',
      label: `Üretim Hareketleri (${stokData.length})`,
      children: (
        <Table
          columns={stokColumns}
          dataSource={stokData}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          locale={{ emptyText: <Empty description="Üretim hareketi bulunamadı" /> }}
          scroll={{ x: 750 }}
        />
      ),
    },
    {
      key: 'satis',
      label: `Satış Kalemleri (${satisData.length})`,
      children: (
        <Table
          columns={satisColumns}
          dataSource={satisData}
          rowKey="id"
          size="small"
          pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50'] }}
          locale={{ emptyText: <Empty description="Satış kalemi bulunamadı" /> }}
          scroll={{ x: 750 }}
        />
      ),
    },
  ];

  const footer = onDelete ? (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Popconfirm
        title="Ürünü Sil"
        description="Bu ürünü silmek istediğinize emin misiniz?"
        onConfirm={handleSil}
        okText="Evet, sil"
        cancelText="Vazgeç"
        okButtonProps={{ danger: true }}
      >
        <Button danger icon={<DeleteOutlined />} loading={silLoading}>
          Ürünü Sil
        </Button>
      </Popconfirm>
      <Button onClick={onClose}>Kapat</Button>
    </div>
  ) : null;

  return (
    <Modal
      title={urun ? `${urun.urun_adi} — Detay` : 'Ürün Detay'}
      open={open}
      onCancel={onClose}
      footer={footer}
      width={950}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <Tabs items={tabItems} defaultActiveKey="stok" />
      )}
    </Modal>
  );
}

export default UrunDetayModal;
